/**
 * Dates as an Indian accountant reads them, and periods as they think in.
 *
 * ── WHY THIS IS A FILE AND NOT A ONE-LINER PER PAGE ──
 * The app was rendering `2026-08-31` everywhere, because that is what the API
 * returns and it is already sorted. An Indian CA reads `31-08-2026`. It is a
 * small thing that is wrong on every screen at once, and the only way to fix it
 * once is for there to be one function.
 *
 * ── THE API FORMAT NEVER CHANGES ──
 * Everything crossing the wire stays `YYYY-MM-DD`: it sorts as a string, it
 * compares as a string, and every date column in the database is that shape.
 * These helpers are display-only, and `toApiDate` is the way back.
 *
 * ── NO TIMEZONE CONVERSION ANYWHERE HERE ──
 * An invoice dated the 1st in Chennai must not render as the 31st in London.
 * A `YYYY-MM-DD` from the API is a calendar date with no time in it, so it is
 * split on the hyphens rather than fed to `new Date()`, which would interpret
 * it as midnight UTC and shift it for anyone west of Greenwich.
 */

/**
 * Today, as a calendar date, in the timezone the books are kept in.
 *
 * ── THE BUG THIS FIXES ──
 * Three screens defaulted their date field to
 * `new Date().toISOString().slice(0, 10)`, which is today in **UTC** - not
 * today where the user is standing. For an Indian business that is wrong for
 * five and a half hours of every single day: at 01:00 IST, UTC is still
 * yesterday, so an invoice raised then was DATED YESTERDAY.
 *
 * That is not cosmetic. The server derives status against today in the org's
 * timezone, so a net-0 invoice dated yesterday is `overdue` the moment it is
 * saved. Worse, at a month or quarter boundary the invoice lands in the wrong
 * GST return period - an invoice raised at 01:00 IST on 1 April is dated
 * 31 March, in the previous financial year, and if that period is locked the
 * save is refused with an error about a date the user never chose.
 *
 * ── WHY `en-CA` ──
 * It is the locale whose short date format IS `YYYY-MM-DD`, so this produces
 * the API's own format directly rather than assembling it from parts and
 * getting the zero-padding wrong.
 *
 * Passing the org's timezone matters for the accountant case: someone in
 * London working on a Chennai client's books should get the client's date, not
 * their own. With no timezone given it falls back to the browser's, which is
 * right for the ordinary case and still never UTC.
 */
export function todayInTimezone(timeZone?: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    // An unknown timezone string from settings must not blank the date field.
    // The browser's own zone is a good answer and is still not UTC.
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  }
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/** `2026-08-31` -> `31-08-2026`. Anything unparseable comes back untouched. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  const [, year, month, day] = match;
  return `${day}-${month}-${year}`;
}

/** `2026-08-31` -> `31 Aug 2026`. For places where the month name reads better. */
export function formatDateLong(value: string | null | undefined): string {
  if (!value) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  const [, year, month, day] = match;
  const name = MONTHS[Number(month) - 1] ?? month;
  return `${day} ${name} ${year}`;
}

/**
 * A timestamp, in the reader's own timezone.
 *
 * Unlike the date helpers this DOES convert, and should: an audit entry
 * happened at an instant, and the reader wants it in their own clock.
 */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return `${day}-${month}-${date.getFullYear()}, ${time}`;
}

/** A Date back to the API's format, using LOCAL parts so the day cannot shift. */
export function toApiDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export const today = (): string => toApiDate(new Date());

// ── Periods ────────────────────────────────────────────────────────────────

export interface Period {
  from: string;
  to: string;
  label: string;
  /** Stable across renders, for a select's value. */
  key: string;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Last day of a month, without constructing a date in the wrong timezone. */
function lastDay(year: number, month1: number): number {
  return new Date(Date.UTC(year, month1, 0)).getUTCDate();
}

export function monthPeriod(year: number, month1: number): Period {
  return {
    from: `${year}-${pad(month1)}-01`,
    to: `${year}-${pad(month1)}-${pad(lastDay(year, month1))}`,
    label: `${MONTHS[month1 - 1]} ${year}`,
    key: `m-${year}-${pad(month1)}`,
  };
}

/**
 * The financial year a date falls in, honouring the org's own start month.
 *
 * `fiscalYearStartMonth` has been in org settings since the first release and
 * nothing has ever read it. April is the Indian default, and an FY that starts
 * in April is labelled by BOTH years - "FY 2026-27" - because "FY 2026" is
 * ambiguous to everybody except the person who wrote it.
 */
export function financialYearOf(date: string, startMonth = 4): { startYear: number; endYear: number } {
  const [year, month] = date.split('-').map(Number);
  const startYear = month >= startMonth ? year : year - 1;
  return { startYear, endYear: startYear + 1 };
}

export function financialYearPeriod(startYear: number, startMonth = 4): Period {
  const endYear = startMonth === 1 ? startYear : startYear + 1;
  const endMonth = startMonth === 1 ? 12 : startMonth - 1;
  return {
    from: `${startYear}-${pad(startMonth)}-01`,
    to: `${endYear}-${pad(endMonth)}-${pad(lastDay(endYear, endMonth))}`,
    label: startMonth === 1 ? `FY ${startYear}` : `FY ${startYear}-${String(endYear).slice(2)}`,
    key: `fy-${startYear}`,
  };
}

/**
 * The four quarters of a financial year, labelled Q1-Q4 from the FY's own
 * start - not from January. A CA asking for "Q1" in India means Apr-Jun.
 */
export function financialYearQuarters(startYear: number, startMonth = 4): Period[] {
  return [0, 1, 2, 3].map((index) => {
    const rawStart = startMonth + index * 3;
    const startYearOfQuarter = startYear + Math.floor((rawStart - 1) / 12);
    const month1 = ((rawStart - 1) % 12) + 1;

    const rawEnd = rawStart + 2;
    const endYearOfQuarter = startYear + Math.floor((rawEnd - 1) / 12);
    const endMonth = ((rawEnd - 1) % 12) + 1;

    const fy = financialYearPeriod(startYear, startMonth);
    return {
      from: `${startYearOfQuarter}-${pad(month1)}-01`,
      to: `${endYearOfQuarter}-${pad(endMonth)}-${pad(lastDay(endYearOfQuarter, endMonth))}`,
      label: `Q${index + 1} ${fy.label}`,
      key: `q-${startYear}-${index + 1}`,
    };
  });
}

/**
 * The list a period picker offers: recent months, then this FY's quarters,
 * then the last two full years.
 *
 * Ordered by what gets picked most. A CA filing GSTR-1 wants last month, and
 * it should be the first thing under the cursor - not buried under a year
 * selector they have to open first.
 */
export function periodOptions(startMonth = 4, from = new Date()): Period[] {
  const options: Period[] = [];

  for (let back = 0; back < 6; back++) {
    const date = new Date(from.getFullYear(), from.getMonth() - back, 1);
    options.push(monthPeriod(date.getFullYear(), date.getMonth() + 1));
  }

  const fy = financialYearOf(toApiDate(from), startMonth);
  options.push(...financialYearQuarters(fy.startYear, startMonth));
  options.push(financialYearPeriod(fy.startYear, startMonth));
  options.push(financialYearPeriod(fy.startYear - 1, startMonth));

  // A month can appear twice - this month is also inside this quarter - and a
  // duplicate <option> value makes a select behave unpredictably.
  const seen = new Set<string>();
  return options.filter((option) => {
    const id = `${option.from}:${option.to}`;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/**
 * The three periods a person reaches for most, as one-tap chips.
 *
 * ── WHY CHIPS AND NOT ONLY THE SELECT ──
 * "This month", "last month" and "this financial year" are what a dashboard
 * is looked at for - the select has them, but behind an open, a scroll and a
 * pick, every time, on a phone. Three chips make each of them one tap, and
 * the select stays for the quarters and the odd month. The labels carry the
 * names ("Sep 2026", "FY 2026-27") so a chip says what it will show rather
 * than only which slot it is.
 */
export function quickPeriods(startMonth = 4, from = new Date()): Array<Period & { short: string }> {
  const fy = financialYearOf(toApiDate(from), startMonth);
  return [
    { ...currentMonth(from), short: 'This month' },
    { ...lastMonth(from), short: 'Last month' },
    { ...financialYearPeriod(fy.startYear, startMonth), short: 'This FY' },
  ];
}

/** The month just gone, which is what a return is almost always for. */
export function lastMonth(from = new Date()): Period {
  const date = new Date(from.getFullYear(), from.getMonth() - 1, 1);
  return monthPeriod(date.getFullYear(), date.getMonth() + 1);
}

/** The month we are in. */
export function currentMonth(from = new Date()): Period {
  return monthPeriod(from.getFullYear(), from.getMonth() + 1);
}
